/**
 * @fileoverview Handles the left-hand navigation buttons.
 */

import { elt } from './dom';
import type { SlTooltip, SlIconButton } from '@shoelace-style/shoelace';
import { ContentSection, NavigationEvent } from './AppState';

class NavigationButton extends HTMLElement {
  tooltip: SlTooltip;
  button: SlIconButton;
  activity: ContentSection;

  static get observedAttributes() {
      return ['tooltip', 'default-icon', 'active-icon', 'active'];
  }

  connectedCallback() {
    this.style.display = 'contents';

    this.button = elt('sl-icon-button');
    this.button.style.fontSize = '2rem';
    this.button.addEventListener('click', this);

    this.tooltip = elt('sl-tooltip', { placement: 'right' }, this.button);
    this.append(this.tooltip);
    this.update();
  }

  attributeChangedCallback() {
    this.update();
  }

  update() {
    // attributeChangedCallback might call into this before connectedCallback, so bail out if
    // my elements haven't been initialized yet
    if (!this.tooltip) {
      return;
    }

    const tooltip = this.getAttribute('tooltip') || '';
    const active = this.hasAttribute('active');
    const defaultIcon = this.getAttribute('default-icon') || '';
    const activeIcon = this.getAttribute('active-icon') || defaultIcon || '';

    this.tooltip.setAttribute('content', tooltip);
    if (active) {
      this.button.setAttribute('name', activeIcon);
      this.button.classList.add('nav-active');
    }
    else {
      this.button.setAttribute('name', defaultIcon);
      this.button.classList.remove('nav-active');
    }
  }

  handleEvent(e: Event): void {
    if (e.type === 'click') {
      if (this.activity) {
        this.dispatchEvent(new NavigationEvent(this.activity));
      }
    }
  }
}

function registerComponents() {
  customElements.define('rebuild-navigation-button', NavigationButton);
}

export class RebuildNavigation extends HTMLElement {
  pagesButton: NavigationButton;
  devicesButton: NavigationButton;
  eventsButton: NavigationButton;
  contentLibraryButton: NavigationButton;
  dataLoggingButton: NavigationButton;
  settingsButton: NavigationButton;
  activeButton: NavigationButton | null;

  constructor() {
    super();
    this.activeButton = null;
  }

  setActiveActivity(activity: ContentSection): void {
    if (this.activeButton) {
      this.activeButton.removeAttribute('active');
    }

    switch (activity) {
      case ContentSection.Pages:
        this.activeButton = this.pagesButton;
        break;

      case ContentSection.Devices:
        this.activeButton = this.devicesButton;
        break;

      case ContentSection.Events:
        this.activeButton = this.eventsButton;
        break;

      case ContentSection.ContentLibrary:
        this.activeButton = this.contentLibraryButton;
        break;

      case ContentSection.DataLogging:
        this.activeButton = this.dataLoggingButton;
        break;

      case ContentSection.Settings:
        this.activeButton = this.settingsButton;
        break;
    }

    if (this.activeButton) {
      this.activeButton.setAttribute('active', '');
    }
  }

  connectedCallback() {
    registerComponents();

    this.style.display = 'contents';

    this.pagesButton = elt('rebuild-navigation-button', {
      tooltip: 'Pages',
      'default-icon': 'speedometer'
    });
    this.pagesButton.activity = ContentSection.Pages;

    this.devicesButton = elt('rebuild-navigation-button', {
      tooltip: 'Devices',
      'default-icon': 'hdd-network',
      'active-icon': 'hdd-network-fill'
    });
    this.devicesButton.activity = ContentSection.Devices;

    this.eventsButton = elt('rebuild-navigation-button', {
      tooltip: 'Events',
      'default-icon': 'exclamation-triangle',
      'active-icon': 'exclamation-triangle-fill'
    });
    this.eventsButton.activity = ContentSection.Events;

    this.contentLibraryButton = elt('rebuild-navigation-button', {
      tooltip: 'Content Library',
      'default-icon': 'image',
      'active-icon': 'image-fill'
    });
    this.contentLibraryButton.activity = ContentSection.ContentLibrary;

    this.dataLoggingButton = elt('rebuild-navigation-button', {
      tooltip: 'Data Logging',
      'default-icon': 'server'
    });
    this.dataLoggingButton.activity = ContentSection.DataLogging;

    this.settingsButton = elt('rebuild-navigation-button', {
      tooltip: 'Settings',
      'default-icon': 'gear-wide-connected'
    });
    this.settingsButton.activity = ContentSection.Settings;

    this.append(
      this.pagesButton,
      this.devicesButton,
      this.eventsButton,
      this.contentLibraryButton,
      this.dataLoggingButton,
      elt('div', { class: 'flexible-space' }),
      this.settingsButton
    );
  }
}
